import { useMemo } from "react";
import { Card, Flex, FullscreenTakeover, Text, toast } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import {
  useCreateJointProfileMutation,
  useUpdateJointProfileMutation,
} from "~/graphql/supergraphHooks";
import { type ComplianceJointProfileInput } from "~/graphql/supergraphTypes";
import { BackButtonWithinForm } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/BackButtonWithinForm";
import { ProfileDetailsType } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/types";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { JointProfileFields } from "./JointProfileFields";
import { buildSchema } from "./schema";
import { actionsStyle, cardStyle } from "./styles_vanilla.css";
import { type FormValues } from "./types";
import { buildInitialValues, buildMutationData } from "./utils";

type Props = {
  caseId: number;
  parentProfileId?: number;
  complianceProfile?: ProfileDetailsType | null;
  onBack?: () => void;
  onCompleted: () => void;
  saveButtonLabel?: string;
};

const REFETCH_QUERIES = [
  "GetComplianceCase",
  "GetCaseInvestingProfileRiskRating",
  "GetPositions",
];

const JointProfileForm = ({
  caseId,
  parentProfileId,
  complianceProfile,
  onBack,
  onCompleted,
  saveButtonLabel = "Save and exit",
}: Props) => {
  const { arenaAccessId } = useCurrentCaseContext();
  const isEditMode = Boolean(complianceProfile?.id);
  const resolvedParentId =
    parentProfileId ?? complianceProfile?.parentRelationships?.[0]?.parentProfileId;
  const isRootProfile = complianceProfile?.isInvestingProfile || false;

  const [createJointProfile] = useCreateJointProfileMutation({
    fetchPolicy: "network-only",
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
    onError: (e) => toast.error(e.message),
  });

  const [updateJointProfile] = useUpdateJointProfileMutation({
    fetchPolicy: "network-only",
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
    onError: (e) => toast.error(e.message),
  });

  const initialValues = useMemo(
    () => buildInitialValues(complianceProfile),
    [complianceProfile],
  );

  const submitCreate = async (data: ComplianceJointProfileInput) => {
    const response = await createJointProfile({
      variables: {
        arenaAccessId: arenaAccessId ?? 0,
        data: data,
        parentProfileId: resolvedParentId,
      },
    });

    if (response?.errors || !response?.data?.createJointProfile?.id) {
      toast.error("Failed to create joint profile");
      return false;
    }
    toast.success("New profile created successfully");
    return true;
  };

  const submitUpdate = async (data: ComplianceJointProfileInput) => {
    if (!complianceProfile?.id) return false;

    const response = await updateJointProfile({
      variables: {
        data: data,
        profileId: complianceProfile.id,
        parentProfileId: resolvedParentId,
      },
    });

    if (response?.errors || !response?.data?.updateJointProfile?.id) {
      toast.error("Failed to edit joint profile");
      return false;
    }
    toast.success("Profile updated successfully");
    return true;
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const data = buildMutationData(values, caseId, isRootProfile);
      const success = isEditMode ? await submitUpdate(data) : await submitCreate(data);
      if (success) onCompleted();
    } catch {
      // toast already surfaced via onError
    }
  };

  return (
    <FullscreenTakeover.Body>
      <Column size="md">
        <Form
          submitOnlyChangedValues={false}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={buildSchema(isRootProfile)}
        >
          <Card className={cardStyle}>
            {onBack && <BackButtonWithinForm onClick={onBack} displayText="Account type" />}
            <Text variant="h3" marginBottom="md">
              {isEditMode ? "Edit joint ownership" : "New joint ownership"}
            </Text>
            <JointProfileFields isRootProfile={isRootProfile} />
          </Card>
          <Flex justifyContent="flex-end" className={actionsStyle}>
            {onBack && <CancelButton onClick={onBack} />}
            <SubmitButton>{saveButtonLabel}</SubmitButton>
          </Flex>
        </Form>
      </Column>
    </FullscreenTakeover.Body>
  );
};

export default JointProfileForm;
